import {
  isSystemSubdomain,
  permissionsByEntity,
  PrivateRouteContent,
  useCheckPermissions,
} from '@cmg/auth';
import { routeUtil, urlUtil } from '@cmg/common';
import { PageLayout } from '@cmg/design-system';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import routeFactory from '../../../common/util/routeFactory';
import { ServerErrorAlert } from '../../../design-system/alert/ServerErrorAlert';
import AccountRolodexRoute from '../../account-detail/rolodex/AccountRolodexRoute';
import {
  fetchAccount,
  resetAccountDetailDuck,
  selectAccount,
  selectError,
  selectLoading,
} from '../../account-detail/shared/ducks';
import { NoAccessRedirect } from '../../core/RootRouter';
import { selectSelfSubdomain } from '../../shared/ducks';
import AccountDetailDomainObject from './AccountDetailDomainObject';
import { AccountDetailRouteSkeleton } from './AccountDetailRouteSkeleton';
import ApiKeyCreateRoute from './api-keys/api-keys-create/ApiKeyCreateRoute';
import ApiKeyDetailRoute from './api-keys/api-keys-detail/ApiKeyDetailRoute';
import ApiKeyListRoute from './api-keys/api-keys-list/ApiKeyListRoute';
import AccountRoleDetailRoute from './role-detail/AccountRoleDetailRoute';
import AccountRoleListRoute from './role-list/AccountRoleListRoute';
import AccountSecurityRoute from './security/AccountSecurityRoute';
import AzureProviderRoute from './security/identity-providers/azure/AzureProviderRoute';
import LocalLoginProviderRoute from './security/identity-providers/local-login/LocalLoginProviderRoute';
import OpenIdProviderRoute from './security/identity-providers/open-id/OpenIdProviderRoute';
import SamlProviderRoute from './security/identity-providers/saml/SamlProviderRoute';
import AccountSubscriptionRoute from './subscription/AccountSubscriptionRoute';
import AccountUserDetailRoute from './user-detail/AccountUserDetailRoute';
import AccountUserListRoute from './user-list/AccountUserListRoute';

const mapStateToProps = state => ({
  account: selectAccount(state),
  loading: selectLoading(state),
  error: selectError(state),
  selfSubdomain: selectSelfSubdomain(),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchAccount,
      resetAccountDetailDuck,
    },
    dispatch
  ),
});

// Routes below are mounted by RootRouter at `${routeFactory.accountDetail.routePath}/*`, so
// v6 Route paths here must be relative to that base path.
const basePath = routeFactory.accountDetail.routePath;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export type RouteParams = {
  accountSubdomain: string;
};

type Props = StateProps & DispatchProps;

/**
 * Entry point for accessing account detail. All routes associated with viewing/managing a particular account stem from here.
 * Responsibilities:
 *  1. Fetch the account from the id in the url. This comes from the assumption that all routes underneath this will most likely require some info from that account entity.
 *  2. Render all account child routes, which are co-located and nested under the account-detail feature as sub-directories.
 */
export const AccountDetailRoute: React.FC<Props> = ({
  account,
  actions,
  error,
  loading,
  selfSubdomain,
}) => {
  const { accountSubdomain: accountSubdomainFromPath } = useParams<RouteParams>();

  useEffect(() => {
    actions.fetchAccount(accountSubdomainFromPath || selfSubdomain);
    return () => {
      // Start with fresh redux state for the next account the user navigates too
      actions.resetAccountDetailDuck();
    };
  }, [accountSubdomainFromPath, actions, selfSubdomain]);

  /* @todo remove these once permissions restricted routes use PrivateRouteContent */

  const hasFullUserAccess = useCheckPermissions([permissionsByEntity.User.FULL], true);

  const hasFullGlobalUserAccess = useCheckPermissions([permissionsByEntity.GlobalUser.FULL], true);

  const hasAccountAccess = useCheckPermissions([permissionsByEntity.Account.READ], true);

  if (loading) {
    return <AccountDetailRouteSkeleton />;
  }

  if (error) {
    return <ServerErrorAlert error={error} title="Error" />;
  }

  if (!account) {
    return null;
  }

  /**
   * can create user if
   * - logged in user is creating a user in an account within the same subdomain and hasFullUserAccess
   * - or logged in user is creating a user in an account in different subdomain and hasFullGlobalUserAccess
   */

  const canCreateOrEditUser =
    account.subdomain === urlUtil.getAccountSubdomain(window.location)
      ? hasFullUserAccess
      : hasFullGlobalUserAccess;

  const canViewCoverage = !isSystemSubdomain() && hasAccountAccess;

  return (
    <PageLayout header={<AccountDetailDomainObject />}>
      <Routes>
        <Route
          path={`${routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountUsers.routePath
          )}/*`}
          element={
            <PrivateRouteContent
              requireAllPermissions={false}
              requiredPermissions={[
                permissionsByEntity.GlobalUser.READ,
                permissionsByEntity.User.READ,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <AccountUserListRoute canCreateOrEditUser={canCreateOrEditUser} />
            </PrivateRouteContent>
          }
        />

        <Route
          path={routeUtil.getRelativeRoutePath(basePath, routeFactory.accountUserDetail.routePath)}
          element={
            <PrivateRouteContent
              requireAllPermissions={false}
              requiredPermissions={[
                permissionsByEntity.GlobalUser.READ,
                permissionsByEntity.User.READ,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <AccountUserDetailRoute
                canViewCoverage={canViewCoverage}
                canCreateOrEditUser={canCreateOrEditUser}
              />
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeUtil.getRelativeRoutePath(basePath, routeFactory.accountRoles.routePath)}
          element={
            <PrivateRouteContent
              requireAllPermissions={false}
              requiredPermissions={[
                permissionsByEntity.GlobalRole.READ,
                permissionsByEntity.Role.READ,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <AccountRoleListRoute />
            </PrivateRouteContent>
          }
        />

        <Route
          path={routeUtil.getRelativeRoutePath(basePath, routeFactory.accountRoleDetail.routePath)}
          element={
            <PrivateRouteContent
              requireAllPermissions={false}
              requiredPermissions={[
                permissionsByEntity.GlobalRole.READ,
                permissionsByEntity.Role.READ,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <AccountRoleDetailRoute />
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountRoleUsersDetail.routePath
          )}
          element={
            <PrivateRouteContent
              requireAllPermissions={false}
              requiredPermissions={[
                permissionsByEntity.GlobalRole.READ,
                permissionsByEntity.Role.READ,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <AccountRoleDetailRoute />
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountSecurityProviderLocalLogin.routePath
          )}
          element={<LocalLoginProviderRoute />}
        />
        <Route
          path={`${routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountRolodex.routePath
          )}/*`}
          element={
            <PrivateRouteContent
              requiredPermissions={[permissionsByEntity.GlobalAccount.READ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <AccountRolodexRoute />
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountSubscription.routePath
          )}
          element={<AccountSubscriptionRoute />}
        />
        <Route
          path={routeUtil.getRelativeRoutePath(basePath, routeFactory.accountSecurity.routePath)}
          element={<AccountSecurityRoute />}
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountSecurityAzureIdOpenIdProvider.routePath
          )}
          element={<AzureProviderRoute />}
        />

        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountSecuritySaml2pProvider.routePath
          )}
          element={<SamlProviderRoute />}
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountSecurityOpenIdConnectProvider.routePath
          )}
          element={<OpenIdProviderRoute />}
        />
        <Route
          path={routeUtil.getRelativeRoutePath(basePath, routeFactory.accountApiKeys.routePath)}
          element={<ApiKeyListRoute />}
        />
        {}
        {/* @todo remove the ability to create api keys from system admins once this feature is turned on for the account admins */}
        <Route
          path={routeUtil.getRelativeRoutePath(basePath, routeFactory.accountApiKeysNew.routePath)}
          element={
            <PrivateRouteContent
              requireAllPermissions={false}
              requiredPermissions={[
                permissionsByEntity.ApiKey.FULL,
                permissionsByEntity.GlobalApiKey.FULL,
              ]}
              renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
            >
              <ApiKeyCreateRoute />
            </PrivateRouteContent>
          }
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            routeFactory.accountApiKeyDetail.routePath
          )}
          element={<ApiKeyDetailRoute />}
        />
        {/* Default to account subscription when no other routes match */}
        <Route
          path="*"
          element={
            <Navigate
              to={routeFactory.accountSubscription.getUrlPath({
                accountSubdomain: accountSubdomainFromPath ?? selfSubdomain,
              })}
              replace
            />
          }
        />
      </Routes>
    </PageLayout>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountDetailRoute);
